import {
    AccountingReportCycle,
    AccountingReportCycleOptionType,
    AccountingReportCycleTotal,
} from "@common/constants";
import {
    SchedulesReportLayout,
    SchedulesReportRows,
} from "@common/types/reports/schedules-report";

import { DEFAULT_SCHEDULES_REPORT_ROWS } from "./useSchedulesReportParams";

export interface SchedulesReportLayoutState<TCycle> {
    layout: SchedulesReportLayout;
    cycle: TCycle;
}

export function isTotalOnlyCycle(
    cycle: AccountingReportCycleOptionType | undefined,
): boolean {
    return cycle === undefined || cycle === AccountingReportCycleTotal.TOTAL;
}

/** B1: Waterfall cannot be shown as Total only, so picking it on Total only moves the cycle to By month. */
export function resolveLayoutChange<
    TCycle extends AccountingReportCycleOptionType | undefined,
>({
    nextLayout,
    cycle,
}: {
    nextLayout: SchedulesReportLayout;
    cycle: TCycle;
}): SchedulesReportLayoutState<TCycle | AccountingReportCycle.MONTH> {
    if (
        nextLayout === SchedulesReportLayout.waterfall &&
        isTotalOnlyCycle(cycle)
    ) {
        return { layout: nextLayout, cycle: AccountingReportCycle.MONTH };
    }
    return { layout: nextLayout, cycle };
}

/** B1': picking Total only while on Waterfall flips the layout back to Rollforward. */
export function resolveCycleChange<
    TCycle extends AccountingReportCycleOptionType | undefined,
>({
    layout,
    nextCycle,
}: {
    layout: SchedulesReportLayout;
    nextCycle: TCycle;
}): SchedulesReportLayoutState<TCycle> {
    if (
        layout === SchedulesReportLayout.waterfall &&
        isTotalOnlyCycle(nextCycle)
    ) {
        return { layout: SchedulesReportLayout.rollforward, cycle: nextCycle };
    }
    return { layout, cycle: nextCycle };
}

const WATERFALL_ONLY_ROWS = new Set<SchedulesReportRows>([
    SchedulesReportRows.byStartMonth,
    SchedulesReportRows.startMonthSummary,
]);

export function getSchedulesReportRowsOptions(
    layout: SchedulesReportLayout,
): SchedulesReportRows[] {
    return Object.values(SchedulesReportRows).filter(
        (option) =>
            !WATERFALL_ONLY_ROWS.has(option) ||
            layout === SchedulesReportLayout.waterfall,
    );
}

export function buildSchedulesReportLayoutParams(options: {
    layout: SchedulesReportLayout;
    rowsMode: SchedulesReportRows;
}): {
    layout: SchedulesReportLayout;
    rows?: SchedulesReportRows;
} {
    const isRowsModeAvailable = getSchedulesReportRowsOptions(
        options.layout,
    ).includes(options.rowsMode);
    return {
        layout: options.layout,
        ...(!isRowsModeAvailable && {
            rows: DEFAULT_SCHEDULES_REPORT_ROWS,
        }),
    };
}
